/**
 * Theme utilities for managing light/dark mode across the application
 */

type Theme = 'light' | 'dark'

/**
 * Apply a theme to the document and save it to localStorage
 * @param theme - The theme to apply ('light' or 'dark')
 */
const applyTheme = (theme: Theme): void => {
  document.documentElement.setAttribute('data-theme', theme)
  document.documentElement.style.colorScheme = theme
  localStorage.setItem('theme', theme)
}

/**
 * Initialize the theme from localStorage, system preference, or default to 'dark'
 * @returns The applied theme
 */
export const initializeTheme = (): Theme => {
  const savedTheme = localStorage.getItem('theme')
  const validThemes: Theme[] = ['light', 'dark']
  let theme: Theme

  if (savedTheme && validThemes.includes(savedTheme as Theme)) {
    theme = savedTheme as Theme
  } else {
    // Check system preference if no valid saved theme
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches
    theme = prefersDark ? 'dark' : 'light'
  }

  applyTheme(theme)
  return theme
}

/**
 * Toggle between light and dark themes
 * @param currentTheme - The current theme ('light' or 'dark')
 * @returns The new theme after toggling
 */
export const toggleTheme = (currentTheme: Theme): Theme => {
  const newTheme = currentTheme === 'light' ? 'dark' : 'light'
  applyTheme(newTheme)
  return newTheme
}

/**
 * Set a specific theme
 * @param theme - The theme to set ('light' or 'dark')
 */
export const setTheme = (theme: Theme): void => {
  applyTheme(theme)
}
